import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const timestamp = z.string().datetime({ offset: false })
const currency = z.string().regex(/^[A-Z]{3}$/)
const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
}).strict()

export const paymentPathV1Schema = z.enum([
  "invoice_later",
  "approved_terms",
  "ach",
  "check",
  "cash",
  "reusable_method",
])
export const depositKindV1Schema = z.enum(["none", "fixed", "percentage"])
export const paymentRiskTierV1Schema = z.enum(["low", "standard", "elevated", "high"])

export const paymentPolicyPublishV1Schema = base.extend({
  policyVersionId: entityIdSchema,
  policyCode: z.string().regex(/^[A-Z0-9][A-Z0-9._-]{0,31}$/),
  policyVersion: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema.nullable(),
  branchId: branchIdSchema.nullable(),
  billingProfileId: entityIdSchema.nullable(),
  serviceId: entityIdSchema.nullable(),
  effectiveFrom: timestamp,
  effectiveUntil: timestamp.nullable(),
  priority: z.number().int().min(0).max(10_000),
  currency,
  minimumAmountMinor: z.number().int().nonnegative(),
  maximumAmountMinor: z.number().int().nonnegative().nullable(),
  riskTier: paymentRiskTierV1Schema.nullable(),
  paymentPath: paymentPathV1Schema,
  depositKind: depositKindV1Schema,
  depositValue: z.number().int().nonnegative().max(1_000_000_000),
  reusableMethodRequired: z.boolean(),
  termsDays: z.number().int().min(0).max(365).nullable(),
  overrideAllowed: z.boolean(),
  overrideMaximumAmountMinor: z.number().int().nonnegative().nullable(),
}).strict().superRefine((value, context) => {
  if (value.branchId !== null && value.legalEntityId === null) {
    context.addIssue({ code: "custom", message: "A Branch policy requires its Legal Entity." })
  }
  if (value.billingProfileId !== null && value.legalEntityId === null) {
    context.addIssue({ code: "custom", message: "A Billing Profile policy requires its Legal Entity." })
  }
  if (value.effectiveUntil !== null && value.effectiveUntil <= value.effectiveFrom) {
    context.addIssue({ code: "custom", message: "The effective window must be positive." })
  }
  if (
    value.maximumAmountMinor !== null &&
    value.maximumAmountMinor < value.minimumAmountMinor
  ) {
    context.addIssue({ code: "custom", message: "The amount band is invalid." })
  }
  if (
    (value.depositKind === "none" && value.depositValue !== 0) ||
    (value.depositKind === "percentage" && value.depositValue > 10_000)
  ) {
    context.addIssue({ code: "custom", message: "The deposit rule is invalid." })
  }
  if (value.paymentPath === "reusable_method" && !value.reusableMethodRequired) {
    context.addIssue({ code: "custom", message: "Reusable-method path requires a reusable method." })
  }
  if (!value.overrideAllowed && value.overrideMaximumAmountMinor !== null) {
    context.addIssue({ code: "custom", message: "Disabled overrides cannot carry a limit." })
  }
})

export const paymentPolicyEvaluateV1Schema = base.extend({
  decisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  billingProfileId: entityIdSchema,
  serviceIds: z.array(entityIdSchema).min(1).max(50),
  evaluatedAt: timestamp,
  amountMinor: z.number().int().nonnegative(),
  currency,
  riskTier: paymentRiskTierV1Schema,
  targetType: z.enum(["estimate", "estimate_version", "change_order", "service_order"]),
  targetId: entityIdSchema,
}).strict()

export const paymentPolicyOverrideV1Schema = base.extend({
  decisionId: entityIdSchema,
  overrideId: entityIdSchema,
  overrideKind: z.enum(["waive_deposit", "reduce_deposit", "payment_path"]),
  paymentPath: paymentPathV1Schema,
  depositAmountMinor: z.number().int().nonnegative(),
  reason: z.string().trim().min(1).max(1_000),
  expiresAt: timestamp,
}).strict()

export const paymentPolicyDecisionV1Schema = z.object({
  decisionId: entityIdSchema,
  decisionVersion: z.literal(1),
  policyVersionId: entityIdSchema,
  policyVersion: z.number().int().positive(),
  paymentPath: paymentPathV1Schema,
  depositAmountMinor: z.number().int().nonnegative(),
  reusableMethodRequired: z.boolean(),
  termsDays: z.number().int().nonnegative().nullable(),
  inputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
}).strict()

export type PaymentPolicyPublishV1 = z.infer<typeof paymentPolicyPublishV1Schema>
export type PaymentPolicyEvaluateV1 = z.infer<typeof paymentPolicyEvaluateV1Schema>
export type PaymentPolicyOverrideV1 = z.infer<typeof paymentPolicyOverrideV1Schema>
export type PaymentPolicyDecisionV1 = z.infer<typeof paymentPolicyDecisionV1Schema>
